import { IClientInstallation, IProjectInstallation } from '@api/models';
import DocFileIcon from '@components/DocFileIcon/DocFileIcon';
import { Button, Card, CircularProgress, Label } from '@hyperflake/react-ui-library';
import { Popover } from '@library/popover';
import { getFileExtension } from '@utils/file-utils';
import { useFileUploadClientInstallation } from 'modules/client-installation/hooks/useFileUploadClientInstallation';
import { useFileUploadProjectInstallation } from 'modules/project-installation/hooks/useFileUploadProjectInstallation';
import { useState } from 'react';

interface IFileUploadProjectInstallationProps {
    clientInstallationId: string;
    initialData: IProjectInstallation;
    setData: (data: any) => void;
    enableEdit?: boolean;
}

const FileUploadProjectInstallation = (props: IFileUploadProjectInstallationProps) => {
    const { clientInstallationId, initialData, setData, enableEdit = false } = props;
    const { handleAddFiles, handleRemoveFile, isUploading, isRemoving } = useFileUploadProjectInstallation(
        clientInstallationId,
        initialData,
        setData
    );

    const [openPopoverFileId, setOpenPopoverFileId] = useState<string | null>(null);

    return (
        <div>
            <Card className="mt-4">
                <Card.Header>Documents</Card.Header>
                <Card.Body>
                    {enableEdit && (
                        <div className="relative inline-block">
                            <Label>Upload Files</Label>
                            <input
                                type="file"
                                disabled={isUploading}
                                className="mt-2 ml-2"
                                onChange={(event) => handleAddFiles(event.currentTarget.files)}
                            />
                            {(isUploading || isRemoving) && (
                                <div className="absolute top-6 right-72 translate-y-[-50%]">
                                    <CircularProgress size={20} />
                                </div>
                            )}
                        </div>
                    )}

                    <ul className="mt-4 list-disc pl-5">
                        {initialData.files.map((file: any, index: number) => (
                            <div key={file._id}>
                                <li key={file._id} className="flex items-center gap-4 mt-2">
                                    <DocFileIcon className="w-7" extension={getFileExtension(file.name)} />
                                    <div>
                                        {file.name}
                                        <a
                                            href={file.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="ml-4 underline text-primary text-sm"
                                        >
                                            View
                                        </a>
                                    </div>

                                    {/* Remove Button that triggers Popover */}
                                    <Popover
                                        placement="top"
                                        contentClassName="px-6 py-5 rounded-2xl shadow-lg w-[380px]"
                                        open={openPopoverFileId === file._id}
                                        setOpen={(open) => setOpenPopoverFileId(open ? file._id : null)}
                                        content={
                                            <div>
                                                <p className="text-sm text-grayscale-500">
                                                    Are you sure you want to remove this file?
                                                </p>

                                                <div className="flex gap-3 mt-4">
                                                    <Button
                                                        size="xs"
                                                        color="primary"
                                                        onClick={() => {
                                                            handleRemoveFile(file._id);
                                                            setOpenPopoverFileId(null);
                                                        }}
                                                    >
                                                        Delete
                                                    </Button>
                                                    <Button
                                                        size="xs"
                                                        variant="shaded"
                                                        onClick={() => setOpenPopoverFileId(null)}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        }
                                    >
                                        {enableEdit && (
                                            <Button size="xs" disabled={isRemoving}>
                                                Remove
                                            </Button>
                                        )}
                                    </Popover>
                                </li>
                                {index !== initialData.files.length - 1 && (
                                    <div className="border-b border-gray-200 my-2"></div>
                                )}
                            </div>
                        ))}
                    </ul>
                </Card.Body>
            </Card>
        </div>
    );
};

export default FileUploadProjectInstallation;
